import { observer } from 'mobx-react-lite'
import { ReactElement, useEffect, useRef, useState } from 'react'
import { OrderResponse } from '../../../../src/api/cart/interfaces'
import { OrderConfirmationSummaryPanel } from './components/OrderConfirmationSummaryPanel'
import {
  getOrderConfirmation,
  getSummaryGrandTotal,
  getVehiclesSummaryInfo,
} from '../orderConfirmationUtils'
import { StoreInstances } from '../../../../src/store/StoreInstancesContainer'
import { breakpoints } from '../../../../src/theme/breakpoints'
import styled from 'styled-components'
import { CartItems } from './components/CartItems'
import { Header } from './components/Header'
import { toLocaleDateString } from '../../../../src/helpers/date'
import { useUiStateStore } from '../../../../src/store/uiState/UiStateStore'
import { getMultiSellerVehiclesSummaryInfo } from '../orderConfirmationUtils'
import Receipt from './components/receipt'
import MultiSellerServicesProvider, {
  ISeller,
} from '../../../../src/services/MultiSellerServicesProvider'

const OrderConfirmation = observer((): ReactElement | null => {
  const [orderResponse, setOrderResponse] = useState<OrderResponse>()
  const [sellersData, setSellersData] = useState<ISeller[]>([])

  const printRef = useRef<HTMLDivElement>(null)
  const uiStore = useUiStateStore()

  useEffect(() => {
    const orderConfirmationResponse = getOrderConfirmation()
    if (!orderConfirmationResponse) {
      StoreInstances.uiStore.displayErrorNotification('orderNotFound')
      return
    }
    setOrderResponse(orderConfirmationResponse)
  }, [])

  useEffect(() => {
    if (!uiStore?.isMultisellerUser()) {
      return
    }
    MultiSellerServicesProvider.getActiveSellers()
      .then((response) => {
        setSellersData(response)
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error(err)
      })
  }, [uiStore])

  if (!orderResponse) {
    return null
  }

  const vehiclesInfo = getVehiclesSummaryInfo(orderResponse.orderDetails)

  const grandTotal =
    orderResponse.orderSummary?.grandTotal ?? getSummaryGrandTotal(vehiclesInfo)
  const multiSellerVehiclesInfo = getMultiSellerVehiclesSummaryInfo(
    orderResponse.orderDetails
  )

  return (
    <Wrapper>
      <ReceiptWrapper ref={printRef}>
        <Receipt
          showYourCost={uiStore.showYourCost()}
          vehicles={multiSellerVehiclesInfo}
          grandTotal={grandTotal}
          orderResponse={orderResponse}
          sellersData={sellersData}
        />
      </ReceiptWrapper>
      <LeftPanel>
        <Header
          title="orderConfirmation"
          orderDate={toLocaleDateString(new Date())}
          showYourCost={uiStore.showYourCost()}
          setShowYourCost={() => uiStore.updateYourCost()}
          printRef={printRef}
          orderResponse={orderResponse}
        />
        {orderResponse.orderDetails?.map((orderDetails, index) => {
          return (
            <CartItems
              key={`order_${index}`}
              showYourCost={uiStore.showYourCost()}
              orderDetails={orderDetails}
              vehicleInfo={multiSellerVehiclesInfo[index]}
              sellersData={sellersData}
            />
          )
        })}
      </LeftPanel>
      <RightPanel>
        <OrderConfirmationSummaryPanel
          header="orderSummary"
          grandTotal={grandTotal}
          vehicles={multiSellerVehiclesInfo}
          orderResponse={orderResponse}
          showYourCost={uiStore.showYourCost()}
        />
      </RightPanel>
    </Wrapper>
  )
})

OrderConfirmation.displayName = 'OrderConfirmation'

export default OrderConfirmation

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  padding: 16px 20px 20px 20px;
  font-family: Roboto, serif;
  @media (max-width: ${breakpoints.laptop}px) {
    flex-direction: column-reverse;
    margin-top: 20px;
  }

  @media print {
    flex-direction: column;
    padding: 0;
    margin: 0;
  }
`

const LeftPanel = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  margin-right: 20px;
  @media print, (max-width: 1200px) {
    margin-right: 0;
  }

  @media print {
    display: none;
  }
`

const RightPanel = styled.div`
  min-width: 260px;
  display: flex;
  flex-direction: column;
  @media (max-width: 1200px) {
    margin-top: 3px;
    margin-bottom: 16px;
  }

  @media print {
    display: none;
  }
`

const ReceiptWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;

  @media print {
    display: block;
    width: 100%;
  }
`
